import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, Store as StoreIcon } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'sonner';

import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useBranch } from '../contexts/BranchContext';
import { itemCategoryApiService, ItemCategory } from '../services/itemCategoryApi';
import { storeApiService, Store as StoreDto } from '../services/storeApi';
import { storeInOutReportApiService, StoreInOutDetailDto } from '../services/storeInOutReportApi';

function toIsoStartOfDay(dateStr: string): string {
  return `${dateStr}T00:00:00`;
}

function toIsoEndOfDay(dateStr: string): string {
  return `${dateStr}T23:59:59`;
}

function getDefaultStartDate(): string {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const y = first.getFullYear();
  const m = String(first.getMonth() + 1).padStart(2, '0');
  const day = String(first.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getDefaultEndDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function safeNumber(v: any): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function formatShortDate(isoOrDate: string): string {
  // Accepts "YYYY-MM-DD" or ISO datetime; returns "DD-MM-YYYY"
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return isoOrDate;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

function resolveField(row: StoreInOutDetailDto, keys: string[]): any {
  for (const k of keys) {
    const v = (row as any)?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') return v;
  }
  return undefined;
}

const COLORS = ['#003D64', '#0568BF', '#FD6500', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'];

const StoreDashboardPage: React.FC = () => {
  const { selectedBranch } = useBranch();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<StoreInOutDetailDto[]>([]);

  const [stores, setStores] = useState<StoreDto[]>([]);
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [loadingMasters, setLoadingMasters] = useState(false);

  const [storeId, setStoreId] = useState<string>('all');
  const [categoryName, setCategoryName] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>(getDefaultStartDate());
  const [endDate, setEndDate] = useState<string>(getDefaultEndDate());

  const loadMasters = async () => {
    if (!selectedBranch) return;
    try {
      setLoadingMasters(true);
      const [storesData, categoriesData] = await Promise.all([
        storeApiService.getStoresByBranch(selectedBranch),
        itemCategoryApiService.getItemCategories(),
      ]);
      setStores(storesData || []);
      setCategories(categoriesData || []);
    } catch {
      setStores([]);
      setCategories([]);
    } finally {
      setLoadingMasters(false);
    }
  };

  const loadDashboard = async () => {
    if (!selectedBranch) {
      toast.error('Please select a branch');
      return;
    }
    try {
      setLoading(true);
      const response = await storeInOutReportApiService.getStoreInOutDetails({
        branchId: selectedBranch,
        storeId: storeId !== 'all' ? Number(storeId) : undefined,
        startDate: startDate ? toIsoStartOfDay(startDate) : undefined,
        endDate: endDate ? toIsoEndOfDay(endDate) : undefined,
        categoryName: categoryName !== 'all' ? categoryName : undefined,
      });
      setRows(response.success ? (response.data || []) : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBranch) {
      void loadMasters();
      void loadDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  const normalized = useMemo(() => {
    return rows.map((r) => {
      const movementType = String(
        resolveField(r, ['movementType', 'movement_type', 'transactionType', 'transaction_type', 'type']) ?? ''
      ).toUpperCase();
      const movementDate = resolveField(r, ['movementDate', 'movement_date', 'transactionDate', 'transaction_date', 'createdAt', 'created_at']) as
        | string
        | undefined;
      const storeName = String(resolveField(r, ['storeName', 'store_name', 'store']) ?? '—');
      const itemName = String(
        resolveField(r, ['itemName', 'item_name', 'rawItemName', 'raw_item_name', 'materialName', 'material_name']) ?? '—'
      );
      const category = String(
        resolveField(r, ['categoryName', 'category_name', 'rawItemCategory', 'raw_item_category', 'rawCategoryName', 'raw_category_name']) ?? '—'
      );
      const qty = safeNumber(resolveField(r, ['quantity', 'qty', 'movementQty', 'movement_qty']));
      return { ...r, _movementType: movementType || '—', _movementDate: movementDate || '', _storeName: storeName, _itemName: itemName, _category: category, _qty: qty };
    });
  }, [rows]);

  const inOutTotals = useMemo(() => {
    let inQty = 0;
    let outQty = 0;
    normalized.forEach((r) => {
      if (r._movementType === 'IN') inQty += r._qty;
      else if (r._movementType === 'OUT') outQty += r._qty;
    });
    return { inQty, outQty, net: inQty - outQty };
  }, [normalized]);

  const inOutBarData = useMemo(
    () => [{ name: 'Total', in_qty: inOutTotals.inQty, out_qty: inOutTotals.outQty }],
    [inOutTotals]
  );

  const dailyTrend = useMemo(() => {
    const map = new Map<string, { date: string; in_qty: number; out_qty: number }>();
    normalized.forEach((r) => {
      const dt = r._movementDate ? new Date(r._movementDate) : null;
      if (!dt || Number.isNaN(dt.getTime())) return;
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      const cur = map.get(key) || { date: key, in_qty: 0, out_qty: 0 };
      if (r._movementType === 'IN') cur.in_qty += r._qty;
      if (r._movementType === 'OUT') cur.out_qty += r._qty;
      map.set(key, cur);
    });
    return Array.from(map.values())
      .sort((a, b) => (a.date < b.date ? -1 : 1))
      .map((x) => ({ ...x, label: formatShortDate(x.date) }));
  }, [normalized]);

  const topItems = useMemo(() => {
    const map = new Map<string, number>();
    normalized.forEach((r) => {
      const k = r._itemName || '—';
      map.set(k, (map.get(k) || 0) + r._qty);
    });
    return Array.from(map.entries())
      .map(([name, qty]) => ({ name, qty }))
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 10);
  }, [normalized]);

  const categorySplit = useMemo(() => {
    const map = new Map<string, { name: string; in_qty: number; out_qty: number }>();
    normalized.forEach((r) => {
      const k = r._category || '—';
      const cur = map.get(k) || { name: k, in_qty: 0, out_qty: 0 };
      if (r._movementType === 'IN') cur.in_qty += r._qty;
      if (r._movementType === 'OUT') cur.out_qty += r._qty;
      map.set(k, cur);
    });
    return Array.from(map.values()).sort((a, b) => (b.in_qty + b.out_qty) - (a.in_qty + a.out_qty)).slice(0, 12);
  }, [normalized]);

  const storeSplit = useMemo(() => {
    const map = new Map<string, { name: string; in_qty: number; out_qty: number }>();
    normalized.forEach((r) => {
      const k = r._storeName || '—';
      const cur = map.get(k) || { name: k, in_qty: 0, out_qty: 0 };
      if (r._movementType === 'IN') cur.in_qty += r._qty;
      if (r._movementType === 'OUT') cur.out_qty += r._qty;
      map.set(k, cur);
    });
    return Array.from(map.values()).sort((a, b) => (b.in_qty + b.out_qty) - (a.in_qty + a.out_qty)).slice(0, 12);
  }, [normalized]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <StoreIcon className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Store Dashboard</CardTitle>
                  <div className="text-sm text-gray-600">IN/OUT movement insights from Store IN/OUT Detail</div>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={loadDashboard} disabled={loading} className="border-gray-300 text-gray-700 hover:bg-gray-50">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Refresh
              </Button>
            </div>
          </CardHeader>
        </Card>

        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Store</Label>
                <Select value={storeId} onValueChange={setStoreId} disabled={loadingMasters}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="All stores" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All stores</SelectItem>
                    {stores.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.storeName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Category</Label>
                <Select value={categoryName} onValueChange={setCategoryName} disabled={loadingMasters}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.categoryName}>
                        {c.categoryName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Start date</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">End date</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>

              <div className="flex justify-end">
                <Button className="bg-primary-600 hover:bg-primary-700 text-white" onClick={loadDashboard} disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Apply
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-sm text-gray-600">Total IN Qty</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{inOutTotals.inQty.toLocaleString('en-IN')}</div>
            </CardContent>
          </Card>
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-sm text-gray-600">Total OUT Qty</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{inOutTotals.outQty.toLocaleString('en-IN')}</div>
            </CardContent>
          </Card>
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-sm text-gray-600">Net Qty (IN - OUT)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${inOutTotals.net < 0 ? 'text-red-600' : 'text-green-700'}`}>
                {inOutTotals.net.toLocaleString('en-IN')}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-800">IN vs OUT (Total)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={inOutBarData} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="in_qty" name="IN" fill="#10B981" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="out_qty" name="OUT" fill="#EF4444" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-800">Daily Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={dailyTrend} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="in_qty" name="IN" stroke="#10B981" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="out_qty" name="OUT" stroke="#EF4444" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-800">Top Items (Qty moved)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={topItems} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={220} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="qty" fill="#0568BF" radius={[6, 6, 6, 6]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="border-primary-200 bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-800">Category-wise (IN/OUT)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={categorySplit} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={220} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="in_qty" name="IN" stackId="a" fill="#10B981">
                    {categorySplit.map((_, idx) => (
                      <Cell key={idx} fill="#10B981" />
                    ))}
                  </Bar>
                  <Bar dataKey="out_qty" name="OUT" stackId="a" fill="#EF4444">
                    {categorySplit.map((_, idx) => (
                      <Cell key={idx} fill="#EF4444" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">Store-wise (IN/OUT)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={storeSplit} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 16 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={220} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="in_qty" name="IN" stackId="a" fill="#10B981" />
                <Bar dataKey="out_qty" name="OUT" stackId="a" fill="#EF4444" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StoreDashboardPage;

